import { useState, useEffect, useContext, useRef, useMemo, useCallback } from "react";
import { SiteSettingContext, AutherContext, ToastContext } from "../Context";
import { setState, getState, STORAGE_KEYS, getKbDefaultStorageKey } from "../../Constant/LocalStorage";
import { TENANT_PLATFORM } from "@hiver/bifrost-js/lib";

import { getKbInfo, getMailBoxDetails, getAvailableFeatures, listKnowledgeBases, getDefaultKbApi } from "../../Service/SiteSettingServices";
import { getDomainDetails } from "../../Service/CustomDomainServices";
import { getUsersList } from "../../Service/UsersService";
import {
  brandForm,
  userListForm,
  sitePageForm,
  codeSnippetForm
} from "../../Forms/Forms";
import { DD_OPTIONS } from "../../Components/Dropdown/constants";
import { CHOICE_DATA, FEATURE_FLAG_NEW_AUTH, NO_KB_EXISTS } from "../../Pages/GetStarted/constants";

export default function SiteSettingProvider(props) {
  const [isNewKbMode] = useState(() => new URLSearchParams(window.location.search).get('newKb') === 'true');

  // Bootstraps window.__kbId from URL/sessionStorage and clears stale ids on
  // logout. The initializer must run during the first render (before children
  // make API calls), so we keep useState despite not consuming the return
  // value. The post-merge modal-skip logic now lives in the URL fetch effect
  // below using a fresh URL read instead of this captured mount-time flag.
  // eslint-disable-next-line no-unused-vars
  const [_hasExplicitKbId] = useState(() => {
    const wasLoggedOut = sessionStorage.getItem('kb_logged_out');
    if (wasLoggedOut) {
      sessionStorage.removeItem('__kbId');
    }

    const kbId = new URLSearchParams(window.location.search).get('kbId');
    if (kbId) {
      window.__kbId = parseInt(kbId, 10);
      sessionStorage.setItem('__kbId', kbId);
      return true; // Explicit — came from URL (switchKb or direct link)
    }

    // Restore from sessionStorage for API scoping, but don't treat as explicit
    const stored = sessionStorage.getItem('__kbId');
    if (stored && !wasLoggedOut) {
      window.__kbId = parseInt(stored, 10);
    }
    return false; // Not explicit — restored from session or fresh
  });

  const { admin, setRedirectToken, redirectToken, authToken } =
    useContext(AutherContext);
  const { setNoNet, setShowToast } = useContext(ToastContext);

  const [brandCounter, setBrandCounter] = useState(false);
  const [isKbDeleted, setIsKbDeleted] = useState(false);
  const [isAvailableFeatureLoading, setIsAvailableFeatureLoading] =
    useState(false);
  const [availableFeatures, setAvailableFeatures] = useState({});
  const [retryCount, setRetryCount] = useState(0);
  const [isNewAuthflow, setIsNewAuthFlow] = useState(false);
  const [userCounter, setUserCounter] = useState(false);
  const [siteSetCounter, setSiteSetCounter] = useState(false);
  // Selecting external as default as per figma
  const [kbType, setKbType] = useState(CHOICE_DATA[0]?.ID);
  const kbRef = useRef(kbType);
  const [roleType, setRoleType] = useState(DD_OPTIONS[0]?.id);
  const [siteSettingsData, setSiteSettingsData] = useState(
    isNewKbMode
      ? { isSiteSettingCompleted: false, isBrandingCompleted: false, isUserAdditionCompleted: false }
      : (getState("siteSettingsData") || false)
  );
  const [customDomainDetails, setCustomDomainDetails] = useState(false);
  const [userList, setUserList] = useState(getState("userList") || []);
  const [allUsers, setAllUsers] = useState([]);
  const [brandFormData, setBrandFormData] = useState(
    getState("brandFormData") || false
  );
  const [userFormData, setUserFormData] = useState(
    getState("userFormData") || false
  );
  const [siteFormData, setSiteFormData] = useState(
    getState("siteFormData") || false
  );
  const [codeSnippetFormData, setCodeSnippetFormData] = useState(
    getState("codeSnippetFormData") || []
  );
  const [customDomainCounter, setCustomDomainCounter] = useState(false);
  const [mailboxCounter, setMailboxCounter] = useState(false);
  const [mailboxList, setMailboxList] = useState(false);
  const [brandActionValidation, setBrandActionValidation] = useState(false);
  const [userActionValidation, setUserActionValidation] = useState(false);
  const [isKbConfigFetched, setIsKbConfigFetched] = useState(false);
  const [siteActionSettingValidation, setsiteSettingActionValidation] =
    useState(false);
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [showKbSelector, setShowKbSelector] = useState(false);

  const switchKb = useCallback((kbId) => {
    // No-op when the user re-selects the KB they're already on. The modal
    // calls switchKb on every Continue click, so without this guard a full
    // page reload would fire even when nothing actually changed. Any
    // "Set as default" persistence still happens at the modal call site
    // before this — that side effect is preserved.
    const numericKbId = Number(kbId);
    const currentKbId =
      Number(window.__kbId) ||
      Number(sessionStorage.getItem('__kbId')) ||
      Number(new URLSearchParams(window.location.search).get('kbId')) ||
      null;
    if (numericKbId && currentKbId === numericKbId) {
      // Sync session/window in case they were out of step but skip reload.
      window.__kbId = numericKbId;
      sessionStorage.setItem('__kbId', String(numericKbId));
      return;
    }

    window.__kbId = kbId;
    sessionStorage.setItem('__kbId', String(kbId));
    const url = new URL(window.location.href);
    url.searchParams.set('kbId', String(kbId));
    window.location.href = url.toString();
  }, []);
  
  const { usersMap, userIdsMap } = useMemo( () => {
    const users = allUsers ?? [];
    if ( !users?.length ){
      return { usersMap: {}, userIdsMap: {} };
    }
    const usersMap = {};
    const userIdsMap = {};

    for( const user of users ){
      usersMap[user.userId] = user;
      userIdsMap[user.id] = user.userId;
    }

    return  { usersMap, userIdsMap };
    
  }, [ allUsers ]);

  // refetch kb config by setting fetched flag = false
  const setKbRefetch = useCallback(() => {
    setIsKbConfigFetched(false)
  }, []);

  const fetchAvailableFeatures = () => {
    setIsAvailableFeatureLoading(true);
    getAvailableFeatures()
      .then((response) => {
        const {
          data: { availableFeatures: availableFftr = {} },
        } = response || {};
        window.grexit = {
          ...window?.grexit,
          isNewAuthFlow: availableFftr[FEATURE_FLAG_NEW_AUTH],
        };
        setIsNewAuthFlow(availableFftr[FEATURE_FLAG_NEW_AUTH]);
        setAvailableFeatures(availableFftr);
        setIsAvailableFeatureLoading(false);
        setRetryCount(0);
      })
      .catch((rej) => {
        if (rej.message === "Network Error") {
          setNoNet(true);
        } else {
          setNoNet(false);
          setShowToast("Unable to fetch features for the account.");
        }
        console.error(rej);
        setRetryCount((prev) => prev + 1);
        setIsAvailableFeatureLoading(false);
      });
  };

  useEffect(() => {
    if (siteSettingsData) {
      if (kbRef.current !== kbType) {
        setBrandActionValidation(false);
      } else if (
        JSON.stringify(brandForm(siteSettingsData)) ===
        JSON.stringify(brandForm(brandFormData))
      ) {
        setBrandActionValidation(true);
      } else {
        setBrandActionValidation(false);
      }
    }
  }, [brandFormData, kbType]);

  useEffect(() => {
    if (userFormData) {
      const old = userFormData?.filter((item) => item.knowledgeBaseAssigned);
      if (old?.length > 0) {
        setUserActionValidation(false);
      } else {
        setUserActionValidation(true);
      }
    }
  }, [userFormData]);

  useEffect(() => {
    if (siteSettingsData) {
      if (
        JSON.stringify(siteSettingsData && sitePageForm(siteSettingsData)) ===
          JSON.stringify(siteFormData && sitePageForm(siteFormData)) &&
        JSON.stringify(
          siteSettingsData && codeSnippetForm(siteSettingsData?.codeSnippet)
        ) ===
          JSON.stringify(
            codeSnippetFormData && codeSnippetForm(codeSnippetFormData)
          )
      ) {
        setsiteSettingActionValidation(true);
      } else {
        setsiteSettingActionValidation(false);
      }
    }
  }, [siteFormData, codeSnippetFormData]);

  useEffect(() => {
    if (siteSettingsData) {
      if (brandCounter === "Update") {
        if (Object.keys(siteSettingsData).length > 0) {
          if (!siteSettingsData?.kbType) siteSettingsData.kbType = kbType;
          setBrandFormData(brandForm(siteSettingsData));
        } else {
          setBrandFormData(brandForm({ kbType }));
        }
        setBrandCounter("Updated");
      }
    }
  }, [siteSettingsData, brandCounter]);

  useEffect(() => {
    if (userList) {
      if (userCounter === "Update") {
        setUserFormData(userList && userListForm(userList));
        setUserCounter("Updated");
      }
    }
  }, [userList, userCounter]);

  useEffect(() => {
    if (siteSettingsData) {
      if (siteSetCounter === "Update") {
        setSiteFormData(siteSettingsData && sitePageForm(siteSettingsData));
        setCodeSnippetFormData(
          siteSettingsData?.codeSnippet?.length > 0
            ? codeSnippetForm(siteSettingsData?.codeSnippet)
            : []
        );
        setSiteSetCounter("Updated");
      }
    }
  }, [siteSettingsData, siteSetCounter]);

  const getKbConfig = () => {
    getKbInfo()
      .then((res) => {
        if (brandCounter === false) {
          setBrandCounter("Update");
        }
        if (siteSetCounter === false) {
          setSiteSetCounter("Update");
        }
        if (customDomainCounter === false) {
          setCustomDomainCounter("Update");
        }
        if (mailboxCounter === false) {
          setMailboxCounter("Update");
        }

        const data = res?.data?.KbConfigurations;
        setSiteSettingsData(data);

        // Multi-KB: capture knowledgeBaseId so subsequent API calls are scoped
        if (!window.__kbId && data?.knowledgeBaseId) {
          window.__kbId = data.knowledgeBaseId;
        }

        if (data?.isInternal) {
          setKbType(CHOICE_DATA[0]?.ID);
          kbRef.current = CHOICE_DATA[0]?.ID;
        } else {
          setKbType(CHOICE_DATA[1]?.ID);
          kbRef.current = CHOICE_DATA[1]?.ID;
        }
        setNoNet(false);
        setRedirectToken(true);
        setIsKbConfigFetched(true);
      })
      .catch((rej) => {
        if (rej.message === "Network Error") {
          setNoNet(true);
        } else {
          setNoNet(false);
          if (rej?.status === 404) {
            if (brandCounter === false) {
              setBrandCounter("Update");
            }
            if (rej?.data?.message?.includes(NO_KB_EXISTS)) {
              setIsKbDeleted(true);
            }
            if (userCounter === false) {
              setUserCounter("Update");
            }
            if (siteSetCounter === false) {
              setSiteSetCounter("Update");
            }
            setSiteSettingsData(true);
            setRedirectToken(true);
          } else {
            setSiteSettingsData(false);
            setRedirectToken(false);
          }
        }
        setIsKbConfigFetched(true);
      });
  };

  // Fetch all KBs and server-persisted default once siteSettingsData is available
  useEffect(() => {
    const isSetupComplete = siteSettingsData?.isSiteSettingCompleted;
    if (!isSetupComplete) return;

    let cancelled = false;

    // Fetch KB list and default KB in parallel.
    // The fallbacks below keep the UI usable on failure but log so we don't
    // silently regress (e.g. an Omni user who'd otherwise see an empty
    // dropdown with no console signal — see listKnowledgeBases tenant
    // branching in SiteSettingServices.js).
    Promise.all([
      listKnowledgeBases().catch((err) => {
        console.error('listKnowledgeBases failed:', err);
        return { data: { knowledgeBases: [] } };
      }),
      getDefaultKbApi().catch((err) => {
        console.error('getDefaultKbApi failed:', err);
        return { data: { defaultKbId: null } };
      }),
    ]).then(([kbsRes, defaultRes]) => {
        if (cancelled) return;
        const kbs = (kbsRes?.data?.knowledgeBases ?? []).filter(
          (kb) => !(kb.importJob || kb.import_job)
        );
        setKnowledgeBases(kbs);

        if (kbs.length <= 1) return;

        const activeTenant = getState(STORAGE_KEYS.KB_ACTIVE_TENANT);

        // Import wizard doesn't need a pre-selected KB — skip the modal.
        // (Preserved from qa-kb-zendesk; theirs' refactor to fresh-URL reads
        // didn't carry the /import exemption.)
        const urlParams = new URLSearchParams(window.location.search);
        if (window.location.pathname.includes("/import") || urlParams.get("flow") === "import") return;

        // Read URL/session fresh each run (state captured at mount can be stale
        // across SPA navigations from /login → / after a successful sign-in).
        // The mount-time `hasExplicitKbId` state above is now superseded by this
        // fresh read combined with the pinnedKbId short-circuit below.
        const urlKbId = parseInt(
          new URLSearchParams(window.location.search).get('kbId'),
          10
        ) || null;
        const sessionKbId = parseInt(sessionStorage.getItem('__kbId'), 10) || null;

        // If URL or session already pin a KB, honor it immediately.
        // Do not gate this on the fetched KB list because list APIs can be paginated/capped.
        // This avoids dropping a valid redirected kbId (e.g. older KBs beyond the first page)
        // and getting stuck in an auth/loading loop.
        const pinnedKbId = urlKbId || sessionKbId;
        if (pinnedKbId) {
          // Sync session + window for downstream API scoping in case URL was stripped.
          window.__kbId = pinnedKbId;
          sessionStorage.setItem('__kbId', String(pinnedKbId));
          return;
        }

        // Per-tenant local default — keeps HIG and HOT defaults from
        // bleeding into each other on the same browser. Falls back to the
        // legacy un-scoped key for HIG (GMAIL) so existing users don't
        // lose their default after this upgrade.
        const tenantScopedKey = getKbDefaultStorageKey(activeTenant);
        const tenantLocal = parseInt(localStorage.getItem(tenantScopedKey), 10) || null;
        const legacyLocal =
          activeTenant === TENANT_PLATFORM.GMAIL
            ? parseInt(localStorage.getItem(STORAGE_KEYS.KB_DEFAULT_ID), 10) || null
            : null;
        const localDefault = tenantLocal || legacyLocal;

        const serverDefault = defaultRes?.data?.defaultKbId;
        const defaultKbId = serverDefault || localDefault;

        // If user has a persisted default, validate it still exists and switch to it.
        // The .some check below is the cross-tenant safety net: a stale
        // default from the other tenant won't match this tenant's KBs.
        if (defaultKbId) {
          const defaultExists = kbs.some(
            (kb) => kb.knowledgeBaseId === defaultKbId
          );
          if (defaultExists) {
            switchKb(defaultKbId);
            return;
          }
          // Default KB was deleted or belongs to the other tenant — clean
          // up both tenant-scoped and legacy fallbacks.
          localStorage.removeItem(tenantScopedKey);
          if (activeTenant === TENANT_PLATFORM.GMAIL) {
            localStorage.removeItem(STORAGE_KEYS.KB_DEFAULT_ID);
          }
        }

        // No valid default and no pinned KB — show selector so user can choose.
        setShowKbSelector(true);
      });

    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteSettingsData?.isSiteSettingCompleted]);

  useEffect(() => {
    if (siteSettingsData) {
      getUsersList()
        .then((res) => {
          setNoNet(false);
          if (userCounter === false) {
            setUserCounter("Update");
          }
          let users = res.data?.users;
          setAllUsers(users ?? []);
          if (siteSettingsData?.isSiteSettingCompleted) {
            users = users?.filter(
              (item) =>
                item.role !== "ADMIN" && item.knowledgeBaseAssigned === false
            );
          }

          setUserList(users);
          setUserFormData(users);
        })
        .catch((rej) => {
          if (rej.message === "Network Error") {
            setNoNet(true);
          } else {
            setNoNet(false);
            setUserList([]);
            setUserFormData([]);
            if (userCounter === false) {
              setUserCounter("Update");
            }
          }
        });
    }
  }, [siteSettingsData]);

  useEffect(() => {
    const hasAvailablefeatures = Object.keys(availableFeatures).length;
    const shouldCallAvailableFeatures =
      authToken && !hasAvailablefeatures && !isAvailableFeatureLoading;
    if (admin) {
      if (hasAvailablefeatures && !isKbConfigFetched && (!isNewKbMode || window.__kbId)) {
        getKbConfig();
      }
      // In newKb mode, initialize form defaults without fetching existing KB config
      if (isNewKbMode && !window.__kbId && brandCounter === false) {
        setBrandCounter("Update");
        setUserCounter("Update");
        setSiteSetCounter("Update");
        setRedirectToken(true);
        setIsKbConfigFetched(true);
      }
      if (shouldCallAvailableFeatures && retryCount <= 3)
        fetchAvailableFeatures();
    } else if (!isNewKbMode) {
      setSiteSettingsData(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    admin,
    redirectToken,
    authToken,
    availableFeatures,
    isAvailableFeatureLoading,
    isKbConfigFetched
  ]);

  useEffect(() => {
    if (siteSettingsData) {
      if (customDomainCounter === "Update") {
        getDomainDetails()
          .then((res) => {
            setCustomDomainDetails(res.data);
            if (res?.data && res.data?.isCdnConfiguredWithSsl) {
              setState("customDomainDetails", res.data);
            }
            if (customDomainCounter === "Update") {
              setCustomDomainCounter("Updated");
            }
            setNoNet(false);
          })
          .catch((rej) => {
            if (rej.message === "Network Error") {
              setNoNet(true);
            } else {
              setNoNet(false);
              if (rej.status === 404) {
                setCustomDomainDetails(false);
                if (customDomainCounter === "Update") {
                  setCustomDomainCounter("Updated");
                }
              }
            }
          });
      }
    }
  }, [customDomainCounter]);

  // useEffect(() => {
  //   setState("siteSettingsData", siteSettingsData);
  // }, [siteSettingsData]);

  // useEffect(() => {
  //   setState("userList", userList);
  // }, [userList]);

  // useEffect(() => {
  //   setState("brandCounter", brandCounter);
  // }, [brandCounter]);

  // useEffect(() => {
  //   setState("userFormData", userFormData);
  // }, [userFormData]);

  // useEffect(() => {
  //   setState("siteFormData", siteFormData);
  // }, [siteFormData]);
  // useEffect(() => {
  //   setState("codeSnippetFormData", codeSnippetFormData);
  // }, [codeSnippetFormData]);

  useEffect(() => {
    if (siteSettingsData) {
      if (mailboxCounter === "Update") {
        getMailBoxDetails()
          .then((res) => {
            setNoNet(false);
            setMailboxList(res.data.mailboxes);
            if (mailboxCounter === "Update") {
              setMailboxCounter("Updated");
            }
          })
          .catch((rej) => {
            if (rej.message === "Network Error") {
              setNoNet(true);
            } else {
              setNoNet(false);
              if (rej.status === 404) {
                setMailboxList([]);
                if (mailboxCounter === "Update") {
                  setMailboxCounter("Updated");
                }
              }
            }
          });
      }
    }
  }, [mailboxCounter, siteSettingsData]);
  return (
    <SiteSettingContext.Provider
      value={{
        availableFeatures,
        siteSettingsData,
        mailboxList,
        customDomainDetails,
        setCustomDomainDetails,
        setCustomDomainCounter,
        kbType,
        setKbType,
        userList,
        usersMap,
        userIdsMap,
        userFormData,
        setUserFormData,
        userActionValidation,

        brandFormData,
        setBrandFormData,
        brandActionValidation,

        siteFormData,
        setSiteFormData,
        codeSnippetFormData,
        setCodeSnippetFormData,
        siteActionSettingValidation,

        setKbRefetch,
        isNewAuthflow,
        setUserList,
        setBrandCounter,
        setUserCounter,
        setSiteSetCounter,
        roleType,
        setRoleType,
        isKbDeleted,
        isKbConfigFetched,
        setIsKbConfigFetched,
        knowledgeBases,
        showKbSelector,
        setShowKbSelector,
        switchKb
      }}
    >
      {props.children}
    </SiteSettingContext.Provider>
  );
}
